import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Check, UserPlus, RotateCcw, ExternalLink, Clock, User, Calendar, FileText, History } from 'lucide-react';
import {
  FilterSearch, Button, StatusBadge, EmptyState, ICON_PX, AlertRow,
} from '../ui';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { usePracticeManagement } from '../../contexts/PracticeManagementContext';
import TaskCard, { TIER_LABEL, TIER_COLOR } from '../shared/TaskCard';
import { MiniCalendar } from './OperationalFocusTabs';

const SUB_TABS = ['Current Priorities', 'Calendar'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Sample history data (will be replaced by real audit trail)
const SAMPLE_HISTORY = [
  { text: 'Task created from workflow schedule', time: '2026-04-12 07:00', by: 'System' },
  { text: 'Assigned to Maria Delgado', time: '2026-04-12 07:01', by: 'System' },
  { text: 'Viewed in Priority Queue', time: '2026-04-13 08:15', by: 'Maria Delgado' },
];

function PMPriorityQueueTab({ readOnly = false, initialFilter, navigateTo, onOpenComplete, onOpenDelegate, onOpenSkip, onOpenOrphaned }) {
  const {
    priorityQueue, orphanedTasks, workflowAvailableNodes, scheduleTask,
    staffRoster, delegateTask,
  } = usePracticeManagement();

  const [activeSubTab, setActiveSubTab] = useState(0);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [reschedDate, setReschedDate] = useState('');
  const [reschedOpen, setReschedOpen] = useState(false);
  const reschedBtnRef = useRef(null);

  // Unified stream
  const allTasks = useMemo(() => [...priorityQueue, ...workflowAvailableNodes], [priorityQueue, workflowAvailableNodes]);

  // Bucket split
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayTasks = useMemo(() =>
    allTasks.filter((t) => (t.bucket === 'today' || t.dueDate <= todayStr) && t.bucket !== 'parked' && t.status !== 'completed' && t.status !== 'skipped'),
    [allTasks, todayStr]);
  // Current Priorities filtered
  const currentPriorities = useMemo(() => {
    let result = FilterSearch.filter(todayTasks, search, (t) => [
      t.taskName, t.domain, t.ownerRole?.replace(/_/g, ' '),
      t.resolvedOwner?.name, t.status, t.workflowName,
    ]);
    if (priorityFilter) result = result.filter((t) => t.priority === priorityFilter);
    if (initialFilter?.status === 'overdue') result = result.filter((t) => t.status === 'overdue' || t.overdueHours > 0);
    const pOrder = { high: 0, medium: 1, low: 2 };
    result.sort((a, b) => (pOrder[a.priority] ?? 2) - (pOrder[b.priority] ?? 2));
    return result;
  }, [todayTasks, search, priorityFilter, initialFilter]);

  const highCount = todayTasks.filter((t) => t.priority === 'high').length;
  const medCount = todayTasks.filter((t) => t.priority === 'medium').length;
  const lowCount = todayTasks.filter((t) => t.priority === 'low').length;

  // Click-to-deselect: clicking same task clears selection
  const handleTaskClick = useCallback((task) => {
    setSelectedTask((prev) => prev?.id === task.id ? null : task);
    setNoteText('');
  }, []);

  const handleComplete = useCallback(() => {
    if (selectedTask) onOpenComplete?.(selectedTask);
  }, [selectedTask, onOpenComplete]);
  const handleAssign = useCallback((staffId) => {
    if (selectedTask) { delegateTask?.(selectedTask.id, staffId); }
  }, [selectedTask, delegateTask]);

  const handleRescheduleConfirm = useCallback(() => {
    if (selectedTask && reschedDate) {
      scheduleTask(selectedTask.id, reschedDate);
      setReschedOpen(false);
      setReschedDate('');
    }
  }, [selectedTask, reschedDate, scheduleTask]);

  const availableStaff = useMemo(() =>
    (staffRoster || []).filter((s) => s.available),
    [staffRoster]);


  // Workflow link: navigate to Workflows tab with workflow selected
  const handleWorkflowClick = useCallback(() => {
    if (selectedTask?.workflowId && navigateTo) {
      navigateTo('checklists', { workflowId: selectedTask.workflowId });
    }
  }, [selectedTask, navigateTo]);

  // Calendar state
  const todayDate = useMemo(() => new Date(), []);
  const [calMonth, setCalMonth] = useState(() => new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
  const [calSelectedDay, setCalSelectedDay] = useState(todayDate.getDate());
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  const [calPickerYear, setCalPickerYear] = useState(todayDate.getFullYear());

  const calYear = calMonth.getFullYear();
  const calMo = calMonth.getMonth();

  const calItemsByDay = useMemo(() => {
    const map = {};
    allTasks.forEach((t) => {
      const d = t.dueDate;
      if (!d) return;
      const [y, m, day] = d.split('-').map(Number);
      if (y === calYear && m - 1 === calMo) {
        if (!map[day]) map[day] = [];
        map[day].push(t);
      }
    });
    return map;
  }, [allTasks, calYear, calMo]);

  const calCells = useMemo(() => {
    const firstDow = new Date(calYear, calMo, 1).getDay();
    const daysInMonth = new Date(calYear, calMo + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [calYear, calMo]);

  const calSelectedItems = calItemsByDay[calSelectedDay] || [];

  const t = selectedTask;

  return (
    <div className="flex flex-col h-full py-3">
      {/* Orphaned alert */}
      {orphanedTasks.length > 0 && (
        <div className="shrink-0 mb-2">
          <AlertRow onClick={onOpenOrphaned}>
            {orphanedTasks.length} task{orphanedTasks.length > 1 ? 's' : ''} need coverage — click to assign
          </AlertRow>
        </div>
      )}

      {/* Global search */}
      <div className="shrink-0 mb-2">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search all tasks..." width="full" />
      </div>

      {/* Sub-tab bar */}
      <div className="shrink-0 flex items-center gap-1 border-b border-nn-border mb-2">
        {SUB_TABS.map((tab, i) => (
          <button key={tab} type="button" onClick={() => setActiveSubTab(i)}
            className={`px-3 py-2 text-body-sm font-medium transition-colors relative ${activeSubTab === i ? 'text-aqua' : 'text-text-muted hover:text-text-dark'}`}>
            {tab}
            {i === 0 && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-aqua text-white text-[9px] font-bold">{todayTasks.length}</span>}
            {activeSubTab === i && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-aqua" />}
          </button>
        ))}
      </div>

      {/* 50-50 split */}
      <div className="flex flex-1 min-h-0 gap-3">

        {/* ═══ LEFT 50% ═══ */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* Current Priorities */}
          {activeSubTab === 0 && (
            <>
              <div className="shrink-0 flex border border-nn-border rounded-lg overflow-hidden mb-2">
                {[
                  { key: null, label: 'TOTAL', count: todayTasks.length, color: 'var(--text-muted)' },
                  { key: 'high', label: 'HIGH', count: highCount, color: 'var(--red-critical)' },
                  { key: 'medium', label: 'MEDIUM', count: medCount, color: 'var(--yellow)' },
                  { key: 'low', label: 'LOW', count: lowCount, color: 'var(--green)' },
                ].map((f, i) => (
                  <button key={f.label} type="button" onClick={() => setPriorityFilter(f.key)}
                    className={`flex-1 py-2 px-3 text-center transition-colors ${i > 0 ? 'border-l-2' : ''} ${priorityFilter === f.key ? 'bg-surface-warm' : 'hover:bg-nn-bg-subtle'}`}
                    style={i > 0 ? { borderLeftColor: f.color } : undefined}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: f.color }}>{f.count}</div>
                    <div className="text-[10.5px] font-semibold text-text-muted uppercase tracking-wider">{f.label}</div>
                  </button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto space-y-1">
                {currentPriorities.length === 0 ? (
                  <EmptyState title="No priorities" body="No tasks match your filters." size="sm" />
                ) : currentPriorities.map((task) => (
                  <TaskCard key={task.id} task={task} selected={selectedTask?.id === task.id} onClick={() => handleTaskClick(task)} />
                ))}
              </div>
            </>
          )}

          {/* Calendar — grid on top, task list below */}
          {activeSubTab === 1 && (
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
              {/* Nav row */}
              <div className="flex items-center justify-between mb-2 shrink-0">
                <button type="button" onClick={() => setCalMonth(new Date(calYear, calMo - 1, 1))}
                  className="text-[24px] px-2.5 py-1 text-text-dark hover:bg-nn-bg-subtle rounded-md transition-colors">‹</button>
                <button type="button" onClick={() => { setCalPickerYear(calYear); setCalPickerOpen((v) => !v); }}
                  className={`text-body-sm font-bold px-3 py-1.5 rounded-md transition-colors ${calPickerOpen ? 'bg-nn-bg-subtle' : 'hover:bg-nn-bg-subtle'}`}>
                  {MONTHS[calMo]} {calYear} {calPickerOpen ? '▲' : '▼'}
                </button>
                <button type="button" onClick={() => setCalMonth(new Date(calYear, calMo + 1, 1))}
                  className="text-[24px] px-2.5 py-1 text-text-dark hover:bg-nn-bg-subtle rounded-md transition-colors">›</button>
              </div>

              {/* Month/year picker */}
              {calPickerOpen && (
                <div className="mb-2 bg-surface-warm rounded-lg p-2.5 shrink-0">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <button type="button" onClick={() => setCalPickerYear((y) => y - 1)} className="text-[20px] px-2 hover:bg-nn-bg-subtle rounded">‹</button>
                    <span className="text-body-sm font-bold text-text-dark min-w-[44px] text-center">{calPickerYear}</span>
                    <button type="button" onClick={() => setCalPickerYear((y) => y + 1)} className="text-[20px] px-2 hover:bg-nn-bg-subtle rounded">›</button>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    {MONTHS.map((mn, mi) => (
                      <button key={mn} type="button"
                        onClick={() => { setCalMonth(new Date(calPickerYear, mi, 1)); setCalPickerOpen(false); }}
                        className={`text-[11px] font-semibold py-1.5 rounded-md transition-colors ${
                          mi === calMo && calPickerYear === calYear
                            ? 'bg-aqua text-white'
                            : 'bg-nn-white text-text-dark hover:bg-nn-bg-subtle'
                        }`}>
                        {mn.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Grid */}
              <div className="rounded-lg border border-nn-border overflow-hidden shrink-0">
                {/* Day headers */}
                <div className="grid grid-cols-7 bg-surface-warm border-b border-nn-border">
                  {DAYS.map((d, di) => (
                    <div key={d} className={`text-center text-[10px] font-bold uppercase tracking-wider text-text-muted py-2 ${di < 6 ? 'border-r border-nn-border' : ''}`}>
                      {d}
                    </div>
                  ))}
                </div>
                {/* Day cells */}
                <div className="grid grid-cols-7">
                  {calCells.map((day, idx) => {
                    const col = idx % 7;
                    const totalRows = Math.ceil(calCells.length / 7);
                    const row = Math.floor(idx / 7);
                    const isLastRow = row === totalRows - 1;
                    if (!day) return <div key={`e-${idx}`} className={`min-h-[48px] ${col < 6 ? 'border-r border-nn-border' : ''} ${!isLastRow ? 'border-b border-nn-border' : ''}`} />;
                    const dayItems = calItemsByDay[day] || [];
                    const isSelected = day === calSelectedDay;
                    const isToday = day === todayDate.getDate() && calMo === todayDate.getMonth() && calYear === todayDate.getFullYear();
                    return (
                      <button key={day} type="button" onClick={() => setCalSelectedDay(day)}
                        className={`min-h-[48px] px-1.5 py-1.5 flex flex-col items-start relative outline-none transition-colors ${
                          isSelected ? 'bg-aqua-glow/20' : 'hover:bg-nn-bg-subtle'
                        } ${col < 6 ? 'border-r border-nn-border' : ''} ${!isLastRow ? 'border-b border-nn-border' : ''}`}>
                        <span className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full text-[11.5px] leading-none ${
                          isSelected ? 'bg-aqua text-white font-bold' : isToday ? 'font-bold text-aqua shadow-[0_0_0_1.5px_var(--aqua)]' : 'text-text-dark'
                        }`}>{day}</span>
                        {dayItems.length > 0 && (
                          <span className={`absolute bottom-1.5 right-2 text-[13px] font-bold leading-none ${isSelected ? 'text-aqua' : 'text-text-muted'}`}>
                            {dayItems.length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Day task list below calendar */}
              <div className="mt-3 flex-1 min-h-0 overflow-y-auto">
                <div className="text-body-sm font-bold text-text-dark mb-2">
                  {MONTHS[calMo]} {calSelectedDay} — {calSelectedItems.length} task{calSelectedItems.length !== 1 ? 's' : ''}
                </div>
                {calSelectedItems.length === 0 ? (
                  <p className="text-body-sm text-text-muted italic">No tasks scheduled.</p>
                ) : (
                  <div className="space-y-1.5">
                    {calSelectedItems.map((tk) => (
                      <TaskCard
                        key={tk.id}
                        task={tk}
                        selected={selectedTask?.id === tk.id}
                        onClick={() => handleTaskClick(tk)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ═══ RIGHT 50% — Detail Panel ═══ */}
        <div className="flex-1 min-w-0 rounded-lg border border-nn-border bg-nn-white overflow-hidden flex flex-col">
          {t ? (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-nn-border shrink-0">
                <h2 className="text-heading-sm text-text-dark break-words">{t.taskName || t.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <StatusBadge variant={t.status === 'completed' ? 'on-track' : t.status === 'overdue' ? 'critical' : 'info'}>
                    {(t.status || 'pending').charAt(0).toUpperCase() + (t.status || 'pending').slice(1)}
                  </StatusBadge>
                  {t.criticalityTier && <span className={`text-caption font-medium ${TIER_COLOR[t.criticalityTier] || ''}`}>{TIER_LABEL[t.criticalityTier]}</span>}
                </div>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {/* Description at top (per user request) */}
                {t.description && (
                  <div>
                    <p className="text-body-sm text-text-secondary break-words">{t.description}</p>
                  </div>
                )}

                {/* Task metadata grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-body-sm">
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Domain</span><p className="text-text-dark capitalize mt-0.5">{t.domain}</p></div>
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Due Date</span><p className="text-text-dark mt-0.5 flex items-center gap-1"><Calendar size={ICON_PX.xs} /> {t.dueDate} {t.dueTime}</p></div>
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Owner</span><p className="text-text-dark mt-0.5 flex items-center gap-1"><User size={ICON_PX.xs} /> {t.resolvedOwner?.name || t.ownerRole?.replace(/_/g, ' ') || 'Unassigned'}</p></div>
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Est. Time</span><p className="text-text-dark mt-0.5 flex items-center gap-1"><Clock size={ICON_PX.xs} /> {t.estimatedMinutes} min</p></div>
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Completion Type</span><p className="text-text-dark mt-0.5 flex items-center gap-1"><FileText size={ICON_PX.xs} /> {(t.completionType || '').replace(/_/g, ' + ')}</p></div>
                  <div><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Cadence</span><p className="text-text-dark mt-0.5 capitalize">{t.cadence || 'N/A'}</p></div>
                  {t.toolReference && (
                    <div className="col-span-2"><span className="text-caption font-bold text-text-muted uppercase tracking-wider">Tool Reference</span><p className="text-text-dark mt-0.5 break-words">{t.toolReference}</p></div>
                  )}
                </div>

                {/* Workflow link (clickable → navigates to Workflows tab with workflow selected) */}
                {t.workflowName && (
                  <div>
                    <span className="text-caption font-bold text-text-muted uppercase tracking-wider">Workflow</span>
                    <button type="button" onClick={handleWorkflowClick}
                      className="mt-0.5 flex items-center gap-1.5 text-body-sm text-aqua hover:text-aqua-bright transition-colors">
                      {t.workflowName} <ExternalLink size={ICON_PX.xs} />
                    </button>
                  </div>
                )}

                {/* History (sample data for now) */}
                <div>
                  <span className="text-caption font-bold text-text-muted uppercase tracking-wider flex items-center gap-1">
                    <History size={ICON_PX.xs} /> History
                  </span>
                  <div className="mt-1 space-y-1.5 overflow-y-auto" style={{ maxHeight: '120px' }}>
                    {SAMPLE_HISTORY.map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-caption text-text-muted">
                        <span className="shrink-0 text-text-secondary">{h.time}</span>
                        <span className="break-words">{h.text}</span>
                      </div>
                    ))}
                    {t.completionNote && (
                      <div className="flex items-start gap-2 text-caption text-text-muted">
                        <span className="shrink-0 text-text-secondary">{t.completedAt ? new Date(t.completedAt).toLocaleString() : ''}</span>
                        <span className="break-words">Note: {t.completionNote}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <span className="text-caption font-bold text-text-muted uppercase tracking-wider">Notes</span>
                  <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add notes about this task..." rows={3}
                    className="mt-1 w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40 resize-y"
                    disabled={readOnly} />
                </div>
              </div>

              {/* Action buttons (pinned bottom) */}
              {!readOnly && t.status !== 'completed' && t.status !== 'skipped' && (
                <div className="shrink-0 px-4 py-3 border-t border-nn-border flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={handleComplete}>
                    <Check size={ICON_PX.md} className="mr-1" /> Complete
                  </Button>

                  {/* Assign — inline dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="flex-1">
                        <UserPlus size={ICON_PX.md} className="mr-1" /> Assign
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="min-w-[200px] max-h-[240px] overflow-y-auto">
                      <DropdownMenuLabel className="text-caption">Assign to</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {availableStaff.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => handleAssign(s.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-body-sm hover:bg-nn-bg-subtle rounded-sm transition-colors"
                        >
                          <span className="font-medium text-text-dark">{s.name}</span>
                          <span className="text-text-muted capitalize">{s.role.replace(/_/g, ' ')}</span>
                        </button>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Reschedule — calendar dropdown */}
                  <div className="flex-1 relative">
                    <Button
                      ref={reschedBtnRef}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => { setReschedOpen((p) => !p); setReschedDate(''); }}
                    >
                      <RotateCcw size={ICON_PX.md} className="mr-1" /> Reschedule
                    </Button>
                    {reschedOpen && (
                      <>
                        <div className="fixed inset-0 z-[1049]" onClick={() => setReschedOpen(false)} />
                        <div className="absolute bottom-full mb-2 right-0 z-[1050] bg-nn-white rounded-lg p-3 shadow-lg border border-nn-border" style={{ minWidth: 260 }}>
                          <MiniCalendar value={reschedDate} onChange={setReschedDate} />
                          <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-nn-border">
                            <Button size="sm" variant="outline" onClick={() => setReschedOpen(false)}>Cancel</Button>
                            <Button size="sm" variant="outline" disabled={!reschedDate} onClick={handleRescheduleConfirm}>
                              <Check size={ICON_PX.md} className="mr-1" /> Confirm
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-text-muted text-body-sm p-4">
              Select a task to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PMPriorityQueueTab;
